import { ExclamationCircleFilled } from '@ant-design/icons'
import classnames from 'classnames/bind'
import React, { useCallback, useId, useRef, useState } from 'react'
import { useClickOutside } from '@/react/front/utils/hooks/useClickOutside'
import { useEscapeKey } from '@/react/front/utils/hooks/useEscapeKey'
import Button, { type ButtonVariant } from '../Button'
import styles from './ConfirmPopover.module.css'

const cx = classnames.bind(styles)

export interface ConfirmPopoverProps {
  title: React.ReactNode
  children: React.ReactElement
  onConfirm: () => void
  onCancel?: () => void
  cancelLabel: string
  confirmLabel: string
  confirmVariant?: ButtonVariant
  placement?: 'top' | 'bottom'
  disabled?: boolean
}

const ConfirmPopover: React.FC<ConfirmPopoverProps> = ({
  title,
  children,
  onConfirm,
  onCancel,
  cancelLabel,
  confirmLabel,
  confirmVariant = 'success',
  placement = 'top',
  disabled = false
}) => {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  useClickOutside(wrapperRef, close, open)
  useEscapeKey(close, open)

  const handleCancel = (): void => {
    close()
    onCancel?.()
  }

  const handleConfirm = (): void => {
    onConfirm()
    close()
  }

  const trigger = React.cloneElement(children, {
    onClick: (event: React.MouseEvent) => {
      children.props.onClick?.(event)
      if (!disabled) setOpen(true)
    }
  })

  return (
    <div className={styles.root} ref={wrapperRef}>
      {open && (
        <div
          className={cx('popover', placement)}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className={styles.content}>
            <ExclamationCircleFilled className={styles.icon} aria-hidden="true" />
            <div id={titleId} className={styles.title}>{title}</div>
          </div>
          <div className={styles.actions}>
            <Button variant="outline" size="small" onClick={handleCancel}>
              {cancelLabel}
            </Button>
            <Button variant={confirmVariant} size="small" onClick={handleConfirm}>
              {confirmLabel}
            </Button>
          </div>
          <span className={styles.arrow} aria-hidden="true" />
        </div>
      )}
      {trigger}
    </div>
  )
}

export default ConfirmPopover
